import { useEffect, useMemo } from "react";

import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import useTranslations from "@/shared/lang/useTranslations";

import { useTransfiyaBanks } from "../../hooks/useTransfiyaBanks";
import type { TransfiyaAccountType } from "../../services/paymentsPayout.service";
import type { TransfiyaSendRecipient } from "../../utils/walletSend";
import {
  EMPTY_TRANSFIYA_VALUES,
  TRANSFIYA_ACCOUNT_TYPES,
  TRANSFIYA_DOCUMENT_TYPES,
  transfiyaRecipientSchema,
  type TransfiyaRecipientValues,
} from "./transfiyaRecipient.schema";

const ACCOUNT_TYPE_LABEL = {
  svgs: "tf_account_type_savings",
  cacc: "tf_account_type_checking",
  dbmo: "tf_account_type_deposit",
  dord: "tf_account_type_standard_deposit",
  dbmi: "tf_account_type_inclusive_deposit",
} as const satisfies Record<TransfiyaAccountType, string>;

export interface TransfiyaRecipientFormProps {
  defaults: TransfiyaRecipientValues;
  /** Every edit: the raw values, and the recipient once they are complete. */
  onChange: (values: TransfiyaRecipientValues, recipient: TransfiyaSendRecipient | null) => void;
}

const digitsOnly = (raw: string) => raw.replace(/\D/g, "");

export function TransfiyaRecipientForm({ defaults, onChange }: TransfiyaRecipientFormProps) {
  const { t } = useTranslations("dispersions");
  const { banks, isLoading, failed } = useTransfiyaBanks(true);
  const schema = useMemo(
    () =>
      transfiyaRecipientSchema({
        bank: t("error_tf_bank_required"),
        accountNumber: t("error_tf_account_number_invalid"),
        name: t("send_tf_name_error"),
        documentNumber: t("error_tf_doc_number_invalid"),
      }),
    [t],
  );
  const form = useZodForm(schema, defaults);

  useEffect(() => {
    const report = (values: Partial<TransfiyaRecipientValues>) => {
      const full = { ...EMPTY_TRANSFIYA_VALUES, ...values } as TransfiyaRecipientValues;
      const parsed = schema.safeParse(full);
      const bankName = banks?.find((bank) => bank.code === full.bankCode)?.name ?? full.bankCode;
      onChange(full, parsed.success ? { rail: "TRANSFIYA", ...parsed.data, bankName } : null);
    };
    report(form.getValues());
    const subscription = form.watch((values) => report(values as Partial<TransfiyaRecipientValues>));
    return () => subscription.unsubscribe();
  }, [form, schema, banks, onChange]);

  const bankItems = (banks ?? []).map((bank) => ({ value: bank.code, label: bank.name }));

  return (
    <ColocaForm form={form} onSubmit={() => undefined} className="space-y-4">
      <ColocaSelectField
        name="bankCode"
        label={t("step2_tf_bank_label")}
        placeholder={isLoading ? t("step2_tf_bank_loading") : t("step2_tf_bank_placeholder")}
        description={failed ? t("step2_tf_bank_error") : undefined}
        items={bankItems}
        disabled={isLoading || failed}
        required
      />
      <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,12rem)_minmax(0,1fr)]">
        <ColocaSelectField
          name="accountType"
          label={t("step2_tf_account_type_label")}
          items={TRANSFIYA_ACCOUNT_TYPES.map((type) => ({ value: type, label: t(ACCOUNT_TYPE_LABEL[type]) }))}
          required
        />
        <ColocaInputField
          name="accountNumber"
          label={t("step2_tf_account_number_label")}
          placeholder={t("step2_tf_account_number_placeholder")}
          inputMode="numeric"
          maxLength={34}
          transform={digitsOnly}
          required
        />
      </div>
      <ColocaInputField
        name="beneficiaryName"
        label={t("send_tf_name_label")}
        placeholder={t("send_tf_name_placeholder")}
        maxLength={120}
        autoComplete="off"
        required
      />
      <div className="grid items-start gap-4 sm:grid-cols-[minmax(0,8rem)_minmax(0,1fr)]">
        <ColocaSelectField
          name="documentType"
          label={t("step2_tf_doc_type_label")}
          items={TRANSFIYA_DOCUMENT_TYPES.map((type) => ({ value: type, label: type }))}
          required
        />
        <ColocaInputField
          name="documentNumber"
          label={t("step2_tf_doc_number_label")}
          placeholder={t("step2_tf_doc_number_placeholder")}
          inputMode="numeric"
          maxLength={20}
          transform={digitsOnly}
          required
        />
      </div>
    </ColocaForm>
  );
}
